import React from 'react';
import { Button } from '../../components/ui/button';
import { computePrevStep } from './wizardNavLogic';

export { computePrevStep };

/**
 * Previous / Continue buttons for the Quote wizard.
 *
 * Encapsulates the step transitions:
 *  - Page 3 (step 6) → back to combined Size+Qty (step 4)
 *  - Combined Size+Qty (step 4 OR 5) → back to Material (step 3),
 *    OR to Format (step 2) when Material was auto-skipped (single-
 *    option), OR to Category (step 1) when both Material AND Format
 *    were auto-skipped (single-format category with single-material
 *    format — e.g., a future category that has only one product).
 *  - Material (step 3) → back to Format (step 2), OR to Category
 *    (step 1) when Format was auto-skipped (single-format category).
 *  - Page 2 (steps 4/5) Continue → Shipping (step 6).
 *  - Steps 1 and 2 have no Continue button (categories/formats advance
 *    the wizard themselves on selection).
 *
 * iter114s.11 — `singleFormatCategory` lets the back button skip
 * over an auto-skipped Step 2 instead of trapping the customer in an
 * infinite forward-skip loop. Same for `getAvailableMaterials().length
 * === 1` and Step 3.
 *
 * Kept purely presentational so the parent owns all wizard flags.
 */
export const QuoteWizardNav = ({
  step,
  setStep,
  canProceed,
  requiresCustomQuote,
  getAvailableMaterials,
  singleFormatCategory = false,
  // iter114s.68 — Pass the artwork-upload status so we can render a
  // small inline hint under the disabled Continue button on Page 2.
  // The dropzone sits hundreds of pixels up the page; without the
  // hint a customer who's filled width/height/quantity but skipped
  // artwork would see Continue greyed-out with no explanation.
  page2ArtworkId = null,
}) => {
  const onPrev = () => {
    setStep(computePrevStep(step, {
      singleMaterial: getAvailableMaterials().length === 1,
      singleFormatCategory,
    }));
  };

  const onNext = () => {
    if (step === 4 || step === 5) {
      setStep(6);
    } else {
      setStep(step + 1);
    }
  };

  const showContinue =
    step < 6 && step !== 1 && step !== 2 && !requiresCustomQuote();

  const onPage2 = step === 4 || step === 5;
  const disabledForArtwork = onPage2 && !page2ArtworkId;

  return (
    <div className="mt-8 pt-6 border-t">
      <div className="flex justify-between">
        <Button
          variant="outline"
          data-testid="wizard-prev-btn"
          onClick={onPrev}
          disabled={step === 1}
          className="border-[#4A7C59] text-[#4A7C59] hover:bg-green-50 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
        >
          ← Previous
        </Button>
        {showContinue && (
          <Button
            data-testid="wizard-next-btn"
            onClick={onNext}
            disabled={!canProceed()}
            className="bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] hover:from-[#4A7C59] hover:to-[#6FBF73] transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:hover:scale-100"
          >
            Continue →
          </Button>
        )}
      </div>
      {showContinue && disabledForArtwork && (
        <p
          className="text-right text-sm text-[#4A7C59] mt-2 italic"
          data-testid="wizard-artwork-gate-hint"
          aria-live="polite"
        >
          Upload your artwork above to continue.
        </p>
      )}
    </div>
  );
};

export default QuoteWizardNav;
